import React, { useEffect, useState, useRef } from 'react';
import { FaArrowLeft, FaArrowRight, FaBalanceScale, FaBook, FaBookOpen, FaCalendar, FaCashRegister, FaChartArea, FaChartBar, FaFileAlt, FaFileExcel, FaFilePdf, FaFileWord, FaList, FaPiggyBank, FaPrint } from 'react-icons/fa';
import Payments from './payments';
import { SiPivotaltracker } from "react-icons/si";
import TrialBalance from './trialBalance';
import CashBook from './cash';
import DetailedIncomeStatement from './income';
import { formatDate, handlePrint, rootUrl } from '../../../helpers';
import axios from 'axios';
import { useAuth } from '../../../context/auth-context';
import BalanceSheet from './balance';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Ipsas from './ipsas';
import BankReconciliation from './bankrec';
import Receipts from './receipts';
import Projects from './projects';
import { MdOutlinePayments } from "react-icons/md";
import { GrProjects } from "react-icons/gr";
import { BiCategory, BiMath } from "react-icons/bi";
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { FaBookAtlas, FaBookBookmark, FaBookJournalWhills, FaListCheck } from 'react-icons/fa6';
import BillReport from './bills';
import SummaryCash from './summaryCash';
import Department from './department';
import Pivot from './pivot';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('randc');
  const [activeSection, setActiveSection] = useState('reports1');
  const [rdata, setData] = useState([]);
  const [userReportRoles, setUserReportRoles] = useState([]);
  const [triggerFetch, setTriggerFetch] = useState(0);
  const auth = useAuth();
  const componentRef = useRef();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchData = async () => {
    try {
      const response = await axios.get(rootUrl(`/trialBalance/${auth.user.cid}~${startDate}~${endDate}`));
      setData(response.data);
      // Trigger child components to fetch their data
      setTriggerFetch(prev => prev + 1);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchUserReportRoles = async () => {
    try {
      const response = await axios.get(rootUrl(`/getUserReportRolesWithDetails/${auth.user.id}`));
      if (response.status === 200) {
        setUserReportRoles(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching user report roles:", error);
    }
  };

  useEffect(() => {   
    fetchUserReportRoles();
  }, []);

  // Remove the automatic data fetching useEffect

  // Check if user has access to a specific report
  const hasReportAccess = (reportName) => {
    return userReportRoles.some(role => role.name === reportName);
  };

  // Filter reports based on user's roles
  const getAvailableReports = () => {
    const allReports = [
      { name: 'randc', label: 'Receipts/Payments', icon: <MdOutlinePayments className='mr-1'/>, section: 'reports1' },
      { name: 'pivot', label: 'Pivot', icon: <SiPivotaltracker className='mr-1'/>, section: 'reports1' },
      { name: 'tb', label: 'Trial Balance', icon: <FaBalanceScale className='mr-1'/>, section: 'reports1' },
      { name: 'cash', label: 'Ledgers & Cashbooks', icon: <FaBookBookmark className='mr-1'/>, section: 'reports1' },
      { name: 'income', label: 'Income Statement', icon: <FaFileAlt className='mr-1'/>, section: 'reports1' },
      { name: 'balance', label: 'Financial Position', icon: <FaBalanceScale className='mr-1'/>, section: 'reports2' },
      { name: 'bills', label: 'Bills', icon: <FaListCheck className='mr-1'/>, section: 'reports2' },
      { name: 'projects', label: 'Projects', icon: <GrProjects className='mr-1'/>, section: 'reports2' },
      { name: 'department', label: 'Department', icon: <BiCategory className='mr-1'/>, section: 'reports2' },
      { name: 'ipsas', label: 'Ipsas', icon: <FaFileWord className='mr-1'/>, section: 'reports2' }
    ];

    return allReports.filter(report => hasReportAccess(report.name));
  };

  const availableReports = getAvailableReports();

  // Set default active tab to first available report
  useEffect(() => {
    if (availableReports.length > 0 && !availableReports.find(r => r.name === activeTab)) {
      setActiveTab(availableReports[0].name);
    }
  }, [availableReports]);

  function handleExportToPDF() {
    const input = componentRef.current;
  
    html2canvas(input, { scrollY: -window.scrollY }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
  
      // Calculate the height of the content in the PDF
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgHeight = (canvasHeight * pdfWidth) / canvasWidth;
      let heightLeft = imgHeight;
  
      let position = 0;
  
      // Add the first page
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
  
      // Add more pages if needed
      while (heightLeft > 0) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }
  
      pdf.save(`${activeTab}.pdf`);
    });
  }

  
  

  function handleExportToExcel() {
    const input = componentRef.current;
  
    if (!input) return;
  
    // Extract table or structured data from the ref
    const rows = Array.from(input.querySelectorAll('tr')).map((row) => {
      return Array.from(row.querySelectorAll('th, td')).map((cell) => cell.textContent);
    });
  
    // Create a worksheet and workbook
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
  
    // Append the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  
    // Export the workbook as an Excel file
    XLSX.writeFile(workbook, `${activeTab}.xlsx`);
  }
  
  const renderContent = () => {
    switch (activeTab) {
      case 'randc':
        return <TabbedComponent startDate={startDate} endDate={endDate} rdata={rdata} triggerFetch={triggerFetch} />;
        case 'pivot':
      return <Pivot startDate={startDate} endDate={endDate} />;
      case 'tb':
        return <TrialBalance rdata={rdata} startDate={startDate} endDate={endDate} />;
      case 'cash':
        return <CashComponent startDate={startDate} endDate={endDate} triggerFetch={triggerFetch} />;
      case 'income':
        return <DetailedIncomeStatement rdata={rdata} startDate={startDate} endDate={endDate} />;
      case 'balance':
        return <BalanceSheet rdata={rdata} startDate={startDate} endDate={endDate} />;
        case 'bills':
        return <BillReport startDate={startDate} endDate={endDate} />;
      // case 'bank':
      //   return <BankReconciliation startDate={startDate} endDate={endDate} />;
      case 'ipsas':
        return <Ipsas rdata={rdata} startDate={startDate} endDate={endDate} />;
      case 'department':
          return <Department startDate={startDate} endDate={endDate} />; 
      case 'projects':
        return <Projects startDate={startDate} endDate={endDate} />;
      default:
        return <div>Welcome to Reports</div>;
    }
    
  };

  // If user has no report access, show message
  if (availableReports.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <FaChartBar className="text-gray-500 text-6xl mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">No Report Access</h2>
        <p className="text-gray-600">You don't have access to any reports. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex w-full items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-[25px] text-slate-800 font-bold flex items-center">
            <FaChartBar className="mr-2" /> Reports
          </h1>
        </div>

        <div className="flex">
        <button
          onClick={() => setActiveSection('reports1')}
          className={`px-4 py-2 mx-2 ${activeSection === 'reports1' ? 'bg-blue-950 text-white' : 'bg-gray-200'}`}
        >
          <FaArrowLeft/>
        </button> 
        <button
          onClick={() => setActiveSection('reports2')}
          className={`px-4 py-2 mx-2 ${activeSection === 'reports2' ? 'bg-blue-950 text-white' : 'bg-gray-200'}`}
        >
          <FaArrowRight/>
        </button>
      </div>
      </div>

      {/* Section Selector */}
   

      {/* Tabs for Reports 1 */}
      {activeSection === 'reports1' && (
        <nav className="grid grid-cols-5 m-3 rounded-2xl shadow-lg bg-gray-200 my-5">
          {availableReports.filter(report => report.section === 'reports1').map((report) => (
            <button
              key={report.name}
              onClick={() => setActiveTab(report.name)}
              className={`cursor-pointer py-2 font-semibold flex items-center justify-center ${activeTab === report.name ? 'bg-white rounded-2xl px-2' : ''}`}
            >
              {report.icon} {report.label}
            </button>
          ))}
        </nav>
      )}

      {/* Tabs for Reports 2 */}
      {activeSection === 'reports2' && (
        <nav className="grid grid-cols-5 m-3 rounded-2xl shadow-lg bg-gray-200 my-5">
          {availableReports.filter(report => report.section === 'reports2').map((report) => (
            <button
              key={report.name}
              onClick={() => setActiveTab(report.name)}
              className={`cursor-pointer py-2 font-semibold flex items-center justify-center ${activeTab === report.name ? 'bg-white rounded-2xl px-2' : ''}`}
            >
              {report.icon} {report.label}
            </button>
          ))}
        </nav>
      )}

<hr className="my-4" />

      <div className='flex justify-between items-center'>
        <div className='flex items-center'>
      <label className="text-[12px] text-slate-600 mx-2">From</label>
      <input
            type="date"
            id="from"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-[220px] from-date border rounded-md text-slate-700 px-3 h-[40px] outline-none text-[12px]"
            placeholder="From"
          />
          <label className="text-[12px] text-slate-600 mx-2">To</label>
          <input
            type="date"
            id="to"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-[220px] to-date rounded-md mr-2 text-slate-700 border px-2 h-[40px] outline-none text-[12px]"
            placeholder="To"
          />
          <button
            onClick={() => {
              if (startDate === "" || endDate === "") {
                toast.info("Please select both start and end dates");
                return;
              }
              fetchData();
            }}
            className="ml-4 px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-blue-900 transition-colors"
          >
            Fetch Data
          </button>
</div>

      <div>
      <button
        onClick={handleExportToPDF}
        className="px-4 py-2 m-4 bg-blue-950 text-white rounded-md float-right"
      >
        <FaFilePdf />
      </button>

        <button
          onClick={() => handlePrint(componentRef)}
          className="bg-blue-950 text-white px-4 py-2 rounded float-right"
          >
            <FaPrint/>
          </button>

      <button
        onClick={handleExportToExcel}
        className="px-4 py-2 m-4 bg-blue-950 text-white rounded-md float-right"
      >
        <FaFileExcel />
      </button>
      </div>
          
      
      </div>


<hr className="my-4" />

      

      

      {/* Content Section */}
      <div className="mt-6" ref={componentRef}>
      {(startDate  === "" || endDate === "") ? <><div className="flex flex-col justify-center items-center">
                                        <FaCalendar className="text-gray-500 text-6xl mb-2" />
                                        <p className="text-gray-600">Select dates to view report</p>
      </div></> : renderContent()}
      </div>
    </div>
  );
}

const TabbedComponent = ({startDate, endDate, rdata, triggerFetch}) => {
  const [activeTab, setActiveTab] = useState("payments");
  return (
    <div>
      {/* Tab Navigation */}
      <div className="tabs flex justify-center">
        <button
          className={`tab-button border-2 rounded-lg shadow-lg p-2 m-2 flex items-center ${activeTab === "payments" ? "bg-blue-950 text-white" : "bg-white text-black"}`}
          onClick={() => setActiveTab("payments")}
        >
         <FaChartArea/> COA
        </button>
      
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "payments" && <Payments startDate={startDate} endDate={endDate} rdata={rdata} triggerFetch={triggerFetch} />}
        {activeTab === "receipts" && <Receipts startDate={startDate} endDate={endDate} rdata={rdata} triggerFetch={triggerFetch} />}
      </div>
    </div>
  );
};

const CashComponent = ({startDate, endDate, triggerFetch}) => {
  const [activeTab, setActiveTab] = useState("detailed");
  return (
    <div>
      {/* Tab Navigation */}
      <div className="tabs flex justify-center">
        <button
          className={`tab-button border-2 rounded-lg shadow-lg p-2 m-2 flex items-center ${activeTab === "detailed" ? "bg-blue-950 text-white" : "bg-white text-black"}`}
          onClick={() => setActiveTab("detailed")}
        >
         <FaBookJournalWhills/> Ledgers
        </button>
        <button
          className={`tab-button border-2 rounded-lg shadow-lg p-2 m-2 flex items-center ${activeTab === "summary" ? "bg-blue-950 text-white" : "bg-white text-black"}`}
          onClick={() => setActiveTab("summary")}
        >
        <FaBook/>  Cashbooks
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "detailed" && <CashBook startDate={startDate} endDate={endDate} triggerFetch={triggerFetch} />}
        {activeTab === "summary" && <SummaryCash startDate={startDate} endDate={endDate} triggerFetch={triggerFetch} />}
      </div>
    </div>
  );
};

